import {Injectable} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {EventNotificationSubscriptionDto} from "../../model/dto/entity/event-notification-subscription.dto";
import {CreateEventSubscriptionRequest} from "../../model/dto/api/webhook.interface";
import {WebhookEventTypeEnum} from "../../enum/webhook-event-type.enum";

/**
 * Service for EventNotificationSubscription entity operations.
 * F-033: Webhook and Event Notifications - Manages user subscriptions to campaign events.
 */
@Injectable({
    providedIn: 'root'
})
export class EventNotificationSubscriptionService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/eventNotificationSubscriptions')
    }

    getObjectIcon(): string {
        return 'fa-solid fa-bell';
    }

    /**
     * Creates a new event subscription.
     * F-033: Acceptance Criteria - "Given member status changes, when event is configured,
     * then event is published to message queue"
     */
    createSubscription(request: CreateEventSubscriptionRequest): Observable<EventNotificationSubscriptionDto> {
        return this.postGeneric<EventNotificationSubscriptionDto>(request, '/subscribe');
    }

    /**
     * Deactivates a subscription.
     */
    deactivateSubscription(subscriptionId: number): Observable<void> {
        return this.postGeneric<void>({}, `/${subscriptionId}/deactivate`);
    }

    /**
     * Gets all active subscriptions for a user.
     */
    getSubscriptionsByUser(userId: number): Observable<EventNotificationSubscriptionDto[]> {
        return this.getGeneric<EventNotificationSubscriptionDto[]>(`/byUser/${userId}`);
    }

    /**
     * Gets all active subscriptions for a campaign.
     */
    getSubscriptionsByCampaign(campaignId: number): Observable<EventNotificationSubscriptionDto[]> {
        return this.getGeneric<EventNotificationSubscriptionDto[]>(`/byCampaign/${campaignId}`);
    }

    /**
     * Checks if a user is subscribed to a specific event.
     */
    isSubscribed(userId: number, campaignId: number, eventType: WebhookEventTypeEnum): Observable<boolean> {
        const eventTypeName = WebhookEventTypeEnum[eventType];
        return this.http.get<boolean>(
            `${this.getApiUrl()}/isSubscribed?userId=${userId}&campaignId=${campaignId}&eventType=${eventTypeName}`
        );
    }

    /**
     * Subscribes a user to all events for a campaign.
     */
    subscribeToAllEvents(userId: number, campaignId: number, deliveryChannel: string): Observable<void> {
        return this.http.post<void>(
            `${this.getApiUrl()}/subscribeToAll?userId=${userId}&campaignId=${campaignId}&deliveryChannel=${deliveryChannel}`,
            {}
        );
    }

    /**
     * Unsubscribes a user from all events for a campaign.
     */
    unsubscribeFromCampaign(userId: number, campaignId: number): Observable<void> {
        return this.http.post<void>(
            `${this.getApiUrl()}/unsubscribeFromCampaign?userId=${userId}&campaignId=${campaignId}`,
            {}
        );
    }
}
